import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { PLATFORM_BRAND_NAME } from './platform-brand'
import { DetectedPlatform, IAuthInfoResponse, IStepAndBanner, LookupOutcome, PasswordOutcome, ResetFailureReason, SigninBanner, SigninStep } from './types'

const DETECTED_PLATFORMS: DetectedPlatform[] = ['slack', 'microsoft', 'google']

export const isDetectedPlatform = (value: unknown): value is DetectedPlatform => typeof value === 'string' && DETECTED_PLATFORMS.includes(value as DetectedPlatform)

/**
 * The email input stays editable while the lookup is in flight, so a late response
 * must not label an address the visitor has since changed.
 */
export const isStaleLookup = (params: { requestedEmail: string; currentEmail: string }): boolean => {
  const requested = params.requestedEmail.trim().toLowerCase()
  const current = params.currentEmail.trim().toLowerCase()
  return requested !== current
}

/**
 * The accounts this address can sign in with, in the order they are offered. Older backends
 * answer without the field, and a single entry says no more than `platform` already did, so
 * both leave the one-platform answers below to handle it.
 */
const readSeveralPlatforms = (response: IAuthInfoResponse): DetectedPlatform[] | null => {
  const platforms = response.availablePlatforms ?? []
  const offered = platforms.filter(isDetectedPlatform)
  const hasEmailAccount = platforms.includes(String(PlatformEnum.Email))

  return offered.length + (hasEmailAccount ? 1 : 0) > 1 ? offered : null
}

export const authInfoToOutcome = (response: IAuthInfoResponse | null | undefined): LookupOutcome => {
  if (!response) return { kind: 'unknownError' }

  const severalPlatforms = readSeveralPlatforms(response)
  if (severalPlatforms) {
    return {
      kind: 'severalPlatforms',
      platforms: severalPlatforms,
      // Only an email account has a password to offer, and only the username signs in with it.
      username: response.availablePlatforms?.includes(String(PlatformEnum.Email)) ? response.username : undefined,
      adpEnabled: Array.isArray(response.loginOptions) && response.loginOptions.includes('adp'),
    }
  }

  if (response.platform === PlatformEnum.Email) {
    // Without a username there is nothing to hand to Cognito, so the password step
    // would be a dead end.
    if (!response.username) return { kind: 'unknownError' }
    return {
      kind: 'emailPlatform',
      username: response.username,
      adpEnabled: Array.isArray(response.loginOptions) && response.loginOptions.includes('adp'),
    }
  }

  if (isDetectedPlatform(response.platform)) {
    return { kind: 'otherPlatform', platform: response.platform }
  }

  return { kind: 'unknownError' }
}

const readCompanyName = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined
  const companyName = (data as { companyName?: unknown }).companyName
  return typeof companyName === 'string' && companyName ? companyName : undefined
}

const readDomainPlatform = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined
  const domainPlatform = (data as { domainPlatform?: unknown }).domainPlatform
  return typeof domainPlatform === 'string' && domainPlatform ? domainPlatform : undefined
}

const isAbortedRequest = (error): boolean => error?.message === 'canceled' || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'

export const authInfoErrorToOutcome = (error): LookupOutcome => {
  if (isAbortedRequest(error)) return { kind: 'aborted' }

  const body = error?.response?.data
  const code = body?.code

  if (code === 'UserNotFound') {
    return { kind: 'userNotFound', domainPlatform: readDomainPlatform(body?.data) }
  }

  if (code === 'EmailPlatformUserInactive') {
    return { kind: 'inactive', organizationName: readCompanyName(body?.data) }
  }

  if (code === 'EmailNotValid') {
    return { kind: 'invalidEmail' }
  }

  return { kind: 'unknownError' }
}

export const stepAndBannerFromLookup = (params: { outcome: LookupOutcome; email: string; currentStep: SigninStep }): IStepAndBanner => {
  const { outcome, email, currentStep } = params
  const emailStep: SigninStep = { name: 'email', email }

  switch (outcome.kind) {
    case 'emailPlatform':
      return {
        step: { name: 'password', email, username: outcome.username, adpEnabled: outcome.adpEnabled },
        banner: { kind: 'none' },
      }
    case 'severalPlatforms':
      return {
        step: {
          name: 'choosePlatform',
          email,
          platforms: outcome.platforms,
          username: outcome.username,
          adpEnabled: outcome.adpEnabled,
        },
        banner: { kind: 'none' },
      }
    case 'otherPlatform':
      return {
        step: {
          name: 'platform',
          platform: outcome.platform,
          email,
          organizationName: outcome.organizationName,
          source: 'lookup',
        },
        banner: { kind: 'none' },
      }
    case 'userNotFound':
      return { step: emailStep, banner: { kind: 'noAccount', email } }
    case 'inactive':
      return {
        step: emailStep,
        banner: { kind: 'inactiveAccount', email, organizationName: outcome.organizationName },
      }
    case 'invalidEmail':
      // The field's own rule already passed, so this is the backend refusing the address:
      // malformed, or a domain that cannot receive mail. Saying nothing is a dead end.
      return { step: emailStep, banner: { kind: 'invalidEmail' } }
    case 'aborted':
      return { step: currentStep, banner: { kind: 'none' } }
    default:
      return { step: emailStep, banner: { kind: 'lookupFailed' } }
  }
}

/**
 * The inactive banner offers to ask the administrators; once this browser has asked, it says
 * so instead. Applied to the banner rather than at each of the three places that raise it
 * (the lookup, the platform check, and a Cognito user that no longer exists), so all three
 * answer a returning visitor the same way.
 */
const ASKABLE_BANNERS: SigninBanner['kind'][] = ['inactiveAccount', 'accessNeeded', 'noAccess']

export const bannerAfterAccessRequest = (banner: SigninBanner, retryOn: Date | null): SigninBanner =>
  ASKABLE_BANNERS.includes(banner.kind) && retryOn ? { kind: 'accessAlreadyRequested', retryOn: retryOn.toISOString() } : banner

/**
 * Slack signs in whoever is already signed in to it here, whatever address was typed, and offers
 * no way to pick another account. So a Slack step for an address other than the one this browser
 * last signed in with says so before the button, rather than after the wrong account comes back.
 */
export const previousAccountBanner = (params: { step: SigninStep; previousSlackAccount?: string }): Extract<SigninBanner, { kind: 'previousPlatformAccount' }> | null => {
  const { step, previousSlackAccount } = params
  if (step.name !== 'platform' || step.platform !== 'slack' || !step.email || !previousSlackAccount) return null
  if (previousSlackAccount.toLowerCase() === step.email.toLowerCase()) return null
  return { kind: 'previousPlatformAccount', platform: PLATFORM_BRAND_NAME.slack, previousEmail: previousSlackAccount, email: step.email }
}

const MFA_CHALLENGES = ['SMS_MFA', 'SOFTWARE_TOKEN_MFA']

export const cognitoResultToOutcome = (result): PasswordOutcome => {
  const challengeName = result?.challengeName

  if (MFA_CHALLENGES.includes(challengeName)) return { kind: 'mfaRequired' }
  if (challengeName === 'PASSWORD_RESET_REQUIRED') return { kind: 'passwordResetRequired' }
  if (challengeName === 'NEW_PASSWORD_REQUIRED') {
    return { kind: 'newPasswordRequired', missingAttributes: result?.missingAttributes ?? [] }
  }

  // Any unrecognised next step must not fall through to the happy path with an
  // undefined username, which would corrupt localStorage and the redux user id.
  if (typeof result?.username !== 'string' || !result.username) return { kind: 'unknownError' }

  return { kind: 'signedIn', username: result.username }
}

/**
 * Amplify v6 populates `name` and not `code` on thrown auth errors, which is why the
 * old page's `error.code` branches never fire. Match on `name` only.
 */
export const cognitoErrorToOutcome = (error): PasswordOutcome => {
  switch (error?.name) {
    case 'NotAuthorizedException':
      return { kind: 'incorrectPassword' }
    case 'PasswordResetRequiredException':
      return { kind: 'passwordResetRequired' }
    case 'UserNotFoundException':
      return { kind: 'userNotFound' }
    default:
      return { kind: 'unknownError' }
  }
}

/**
 * Cognito's reset errors, mapped to the reasons the page has copy for. Throttling arrives
 * under three names for the same thing, which is why they are listed rather than matched.
 */
export const resetErrorToReason = (error): ResetFailureReason => {
  switch (error?.name) {
    case 'LimitExceededException':
    case 'TooManyFailedAttemptsException':
    case 'TooManyRequestsException':
      return 'limitExceeded'
    case 'CodeMismatchException':
      return 'codeMismatch'
    case 'ExpiredCodeException':
      return 'codeExpired'
    case 'InvalidPasswordException':
      return 'invalidPassword'
    case 'CodeDeliveryFailureException':
      return 'deliveryFailed'
    default:
      return 'unknown'
  }
}

const MFA_CODE_MISMATCH_ERRORS = ['CodeMismatchException', 'EnableSoftwareTokenMFAException']

/**
 * Whether the code itself was wrong, which is the one case retyping fixes. An expired
 * challenge or a failed request needs the sign-in started again, so they are told apart.
 */
export const isMfaCodeMismatch = (error): boolean => MFA_CODE_MISMATCH_ERRORS.includes(error?.name)

export const stepAndBannerFromPassword = (params: { outcome: PasswordOutcome; currentStep: SigninStep }): IStepAndBanner => {
  const { outcome, currentStep } = params
  const email = 'email' in currentStep ? (currentStep.email ?? '') : ''

  switch (outcome.kind) {
    // The password was right and Cognito is holding a challenge open, so this is a step of the
    // sign-in rather than an interruption to it.
    case 'mfaRequired':
      return { step: { name: 'mfa', email }, banner: { kind: 'none' } }
    case 'incorrectPassword':
      return { step: currentStep, banner: { kind: 'incorrectPassword' } }
    case 'passwordResetRequired':
      return { step: currentStep, banner: { kind: 'passwordResetRequired' } }
    case 'userNotFound':
      // The lookup said this address has an account, but Cognito has no user for it.
      // Never say "deactivated" here: a stale username produces the same error.
      return {
        step: { name: 'email', email },
        banner: { kind: 'inactiveAccount', email },
      }
    case 'unknownError':
      return { step: currentStep, banner: { kind: 'lookupFailed' } }
    default:
      return { step: currentStep, banner: { kind: 'none' } }
  }
}
